import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The read for the Compliance view (task 10.1; design C6, 12.2). It mirrors the Posture screen's
 * hook exactly, over ONE operator-API operation — `GET /api/v1/compliance/mapping`, role `viewer`,
 * taken straight from the generated `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/compliance/mapping?framework=…&from=…&to=…` → {@link ComplianceMappingResponse}:
 *   the requesting tenant's current findings joined against the versioned, tenant-INDEPENDENT
 *   crosswalk for the chosen framework. Per contract it carries `meta` (generated_at, from, to,
 *   data_quality), the `crosswalk` version/hash, the resolved `framework` (id + edition), the
 *   framework's `references` (each with a `finding_count` and the underlying {@link MappedFinding}s),
 *   the `unmapped` count, a `coverage` summary, and the `advisory` disclaimer text.
 *
 * There is NO client-side framework catalog: the four frameworks are the generated
 * {@link ComplianceFramework} enum and the references come entirely from the server response
 * (Requirement 4.5). The mapping is advisory and is never read by any enforcement path.
 *
 * The read is WINDOWED (it takes `from`/`to`) and framework-scoped, so — exactly like the Posture
 * screen — the window is resolved from the shared time-range and pausing freezes the query: in live
 * mode "now" is bucketed to a 10s grid to keep the query key stable between ticks; when frozen
 * (paused or custom) `resolveWindow` returns the exact frozen window and the key stops advancing
 * (design 12.2, FR-13 AC2). The chosen `framework` is part of the key, so switching framework issues
 * a fresh query while the previous window's data is kept until the next resolves.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and
 * the shell shows "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The four target compliance frameworks — the generated enum, the SPA holds no catalog of its own. */
export type ComplianceFramework = components["schemas"]["ComplianceFramework"];
/** The contract `ComplianceMappingResponse` body (framework references + counts + coverage). */
export type ComplianceMappingResponse = components["schemas"]["ComplianceMappingResponse"];
/** A single framework reference row (id, title, finding count, underlying findings). */
export type ComplianceReference = ComplianceMappingResponse["references"][number];
/** A single mapped finding (finding id, signal, severity/tier, crosswalk confidence + rationale). */
export type MappedFinding = components["schemas"]["MappedFinding"];

/** The resolved time window a query ran for, so the view can label the snapshot honestly. */
export interface ComplianceWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseComplianceMappingResult {
  readonly query: UseQueryResult<ComplianceMappingResponse, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: ComplianceWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): ComplianceWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the compliance mapping for the chosen `framework` and the current window. The query key
 * carries the framework and the window so a frozen window never refetches, a live window advances on
 * the 10s grid, and switching framework issues a fresh query; `staleTime` / `refetchInterval` match
 * the other screens (live tolerates a little staleness, frozen never goes stale). The previous
 * window's data is kept while the next live window (or a new framework) fetches, so the view does
 * not blink through its loading state on each ~10s tick.
 */
export function useComplianceMapping(
  framework: ComplianceFramework,
): UseComplianceMappingResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<ComplianceMappingResponse, ApiError>({
    queryKey: ["compliance", "mapping", framework, window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<ComplianceMappingResponse>("/compliance/mapping", {
        query: { framework, from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous framework/window's data while the next one fetches, so the view does not
    // blink through its loading state on each ~10s tick or on a framework switch (seamless refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
